import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import { useAppDispatch } from "src/store/hooks";
import {
  CruiseLineData,
  CruiseLineDataForm,
  CruiseLineEditDataForm,
  addCruiseLine,
  refreshCruiseLineList,
  updateCruiseLine,
} from "src/store/reducers/cruiseLines";
import {
  EmailRequired,
  KeyRequired,
  StringLengthValidation,
} from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";

type ICruiseLineFormCanvas = {
  Title: string;
  show: boolean;
  editId: string;
  hide: () => void;
  cruiseLineData?: CruiseLineData;
};

const CruiseLineFormCanvas = ({
  Title,
  show,
  hide,
  editId,
  cruiseLineData,
}: ICruiseLineFormCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();

  const {
    handleChange,
    setErrors,
    values,
    handleSubmit,
    touched,
    errors,
    resetForm,
  } = useFormik({
    initialValues: {
      cruise_line_name: cruiseLineData?.cruise_line_name || "",
      cruise_line_code: cruiseLineData?.cruise_line_code || "",
      contact_number: cruiseLineData?.contact_number || "",
      contact_person_name: cruiseLineData?.contact_person_name || "",
      email_id: cruiseLineData?.email_id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      cruise_line_name: StringLengthValidation("Cruise line name", 2, 50),
      cruise_line_code: StringLengthValidation("Cruise line code", 2, 10),
      contact_person_name: StringLengthValidation("Contact person", 3, 30),
      contact_number: KeyRequired("Contact number"),
      email_id: EmailRequired("Email address", false),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (editId !== "Add") {
        setPending(true);
        const formValue = { ...values, id: editId };
        editU(formValue);
      } else {
        setPending(true);
        addU(values);
      }
    },
  });

  const handleHide = useCallback(() => {
    hide();
  }, [hide]);

  const addU = useCallback(
    async (values: Omit<CruiseLineDataForm, "id">) => {
      try {
        await dispatch(addCruiseLine(values)).unwrap();
        handleHide();
        toast.success("Cruise line is added successfully");
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  const editU = useCallback(
    async (values: CruiseLineEditDataForm) => {
      try {
        await dispatch(updateCruiseLine(values)).unwrap();
        toast.success("Cruise line is updated successfully");
        dispatch(refreshCruiseLineList());
        handleHide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, handleHide, setErrors]
  );

  useEffect(() => {
    resetForm();
  }, [resetForm, show]);

  return (
    <Offcanvas
      show={show}
      onHide={handleHide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h5 className="modal-title" id="#gridSystemModal">
          {Title}
        </h5>
        <button type="button" className="btn-close" onClick={handleHide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="cruise_line_name"
                  labelText="Cruise Line Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.cruise_line_name}
                  touched={touched.cruise_line_name}
                  errors={errors.cruise_line_name}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="cruise_line_code"
                  labelText="Cruise Line Code"
                  required={true}
                  handleChange={handleChange}
                  value={values.cruise_line_code}
                  touched={touched.cruise_line_code}
                  errors={errors.cruise_line_code}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_person_name"
                  labelText="Contact Person Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.contact_person_name}
                  touched={touched.contact_person_name}
                  errors={errors.contact_person_name}
                />{" "}
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="contact_number"
                  labelText="Contact number"
                  required={true}
                  handleChange={handleChange}
                  value={values.contact_number}
                  touched={touched.contact_number}
                  errors={errors.contact_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="email_id"
                  inputType="email"
                  labelText="Email"
                  handleChange={handleChange}
                  value={values.email_id}
                  touched={touched.email_id}
                  errors={errors.email_id}
                />{" "}
              </div>
              <div>
                <button
                  type="submit"
                  disabled={pending}
                  className="btn btn-primary me-1"
                >
                  Submit
                </button>
                <Link
                  to={"#"}
                  onClick={handleHide}
                  className="btn btn-danger light ms-1"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default CruiseLineFormCanvas;
